import React from "react";
import Papa from "papaparse";
import { Button, Grid, Tooltip, Typography } from "@material-ui/core";
import { Info as InfoIcon } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { useMutation } from "@apollo/client";
import { Link, navigate } from "@reach/router";
import { csvPostprocessor } from "pages/dashboard/admin/import/interest_imports/components/utils";
import { ImportListMutation } from "pages/dashboard/admin/import/interest_imports/queries";
import { ImportListCSVMutation, ImportListCSVMutationVariables } from "types/gql-types";
import { LoadingSpinner } from "components/graphql-views";
import { StepProps } from "..";
import { getListName, useStepData } from "../context";
import StepLayout from "./layout";
import { validateStep } from "./validation";

const StepFinalize: React.FC<StepProps> = () => {
  const [importList, importListResults] = useMutation<
  ImportListCSVMutation,
  ImportListCSVMutationVariables
  >(ImportListMutation);

  const { viewer, stepData, setStepName, importErrors, setImportErrors } = useStepData();
  const { listId } = stepData;

  const handleReset = () => {
    navigate("./upload?reset=true");
  };

  const handleSubmit = async () => {
    if (!stepData.listId) {
      throw new Error("unexpectedly missing listId");
    }
    if (stepData.file === null) {
      throw new Error("unexpectedly missing file object");
    }

    const processed = csvPostprocessor(stepData);
    const serialized = Papa.unparse(processed.data);

    const variables = {
      id: stepData.listId,
      csv: serialized,
    };

    const result = await importList({ variables });
    if (result.errors) {
      console.error("Import failed", result.errors);
    } else {
      // encode total number of records in the URL
      const totalRecords = processed.data.length;
      const encodedRecords = encodeURIComponent(totalRecords.toString());
      const encodedListName = encodeURIComponent(getListName(viewer.lists, stepData.listId));
      const successUrl = `./success?records=${encodedRecords}&list=${encodedListName}`;
      navigate(successUrl, { replace: true });
    }
    return null;
  };

  if (!validateStep(stepData, "finalize")) {
    return null;
  }

  if (!viewer!.lists) return null;

  const listName = viewer!.lists.find(list => list.id === listId)?.name;

  const linkToSettings = `/dash/manager/settings/property/${listId}/lists`;

  React.useEffect(() => {
    setStepName("Finalize");
  }, []);

  React.useEffect(
    () => {
      if (importListResults.error) {
        setImportErrors(importListResults.error.graphQLErrors);
      } else if (importListResults.data) {
        setImportErrors([]);
      }
    }
    , [importListResults.error],
  );

  // Reset error when data changes
  React.useEffect(() => {
    if (stepData) setImportErrors([]);
  }, [stepData]);

  return <StepLayout
    title="Review and finalize your import"
    description={
      <Typography variant="body1">
        {stepData.data.length} applications will be added to {listName}.
      </Typography>}
  >
    <Grid item xs={12}>
      <ul>
        <li><Typography variant="body1"><strong>Imported from:</strong> {stepData.file.name}</Typography></li>
        <li><Typography variant="body1"><strong>New applications:</strong> {stepData.data.length}</Typography></li>
        <li><Typography variant="body1"><strong>Destination List:</strong> {listName}</Typography></li>
        <li><Typography variant="body1"><strong>Date/Time Order:</strong> {stepData.validateOrder ? "Yes" : "No"}</Typography></li>
        <li><Typography variant="body1">
          <Tooltip title="Edit Time Zone in Settings">
            <strong>Time Zone of applications:</strong>
          </Tooltip> {stepData.timezone ? <>
            {" "}{stepData.timezone}<Link to={linkToSettings}><InfoIcon fontSize="small" viewBox="0 0 30 30" /></Link>
          </> : <>
            <Alert severity="warning">
              Timezone not set for this list.
            </Alert>
            <Link to={linkToSettings}>Set Timezone</Link>
          </>}
        </Typography></li>
      </ul>
    </Grid>
    <Grid item xs={12}>
      <Grid item xs={12} container spacing={2}>
        <Grid item>
          <Button variant="contained" color="secondary" onClick={handleReset}>
            Cancel Import
          </Button>
        </Grid>
        <Grid item>
          <Button
            data-cy="complete-import-button"
            variant="contained" color="primary" onClick={handleSubmit} disabled={importListResults.loading}>
            Import
          </Button>
        </Grid>
        {importListResults.loading && (
          <LoadingSpinner />
        )}
        <Grid item xs={12}>
          {importErrors.length > 0 && (
            <Typography color="error">
              {importErrors.map((error) => {
                // @ts-ignore
                if (error.details) {
                  // @ts-ignore
                  return Object.entries(error.details).map(([key, value]) => <>{Number.isInteger(error.row) ? `Row ${error.row}: ` : ""}{key}: {JSON.stringify(value)}<br/></>);
                }
              })}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  </StepLayout>;
};

export default StepFinalize;
